import { router, usePage } from "@inertiajs/react";
import { loginPath } from "@travily/routes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { submitFavoriteToggle } from "@/lib/favorite-toggle";
import type { SharedProps } from "@/types";

type GetFavorited = (type: string, id: string | number, serverFavorited: boolean) => boolean;
type OnFavoriteToggle = (type: string, id: string | number, serverFavorited: boolean) => void;

interface UseSearchFavoritesReturn {
  getFavorited: GetFavorited;
  onFavoriteToggle: OnFavoriteToggle;
}

// A toggle's favorited_ids resync can be lost when the user navigates before
// it completes (Inertia discards an async reload response once the pathname
// changes). Module-scoped so the flag survives page unmounts; the next page
// that carries the prop retries the resync.
let favoritedIdsResyncPending = false;

/**
 * Manages optimistic favorite state for favoritable cards.
 *
 * Server truth lives in the page's `favorited_ids` prop (records and search
 * hits carry no per-user flag, keeping them shareable and cacheable): callers
 * derive `serverFavorited` from the prop — or use `useFavoritedIdsCallbacks`,
 * which does it for them — and this hook refreshes the prop with a partial
 * reload after each toggle.
 *
 * `localFavorites` layers instant optimistic feedback on top and is
 * reconciled away once the matching resync lands.
 */
export function useSearchFavorites(): UseSearchFavoritesReturn {
  const { props } = usePage<SharedProps>();
  const { current_user } = props;
  const hasFavoritedIdsProp = "favorited_ids" in props;
  const [localFavorites, setLocalFavorites] = useState<Record<string, boolean>>({});
  const prevFavorites = useRef(localFavorites);
  prevFavorites.current = localFavorites;

  const reloadFavoritedIds = useCallback((onSuccess?: () => void) => {
    router.reload({
      only: ["favorited_ids"],
      onSuccess: () => {
        favoritedIdsResyncPending = false;
        // A nav prefetch in flight during the toggle can land a pre-toggle
        // response after the 204 handler's flush — flush again now that the
        // toggle has fully settled.
        router.flushAll();
        onSuccess?.();
      },
    });
  }, []);

  useEffect(() => {
    if (favoritedIdsResyncPending && hasFavoritedIdsProp) reloadFavoritedIds();
  }, [hasFavoritedIdsProp, reloadFavoritedIds]);

  const onFavoriteToggle: OnFavoriteToggle = useCallback(
    (favorableType, favorableId, serverFavorited) => {
      if (!current_user) {
        router.visit(loginPath());
        return;
      }

      const key = `${favorableType}:${favorableId}`;
      const wasFavorited = prevFavorites.current[key] ?? serverFavorited;
      setLocalFavorites((prev) => ({ ...prev, [key]: !wasFavorited }));

      submitFavoriteToggle({
        favorableType,
        favorableId,
        wasFavorited,
        onToggled: () => {
          if (!hasFavoritedIdsProp) return;
          favoritedIdsResyncPending = true;
          reloadFavoritedIds(() =>
            // Drop the optimistic override now that the prop carries the
            // toggled state — unless another toggle changed it meanwhile.
            setLocalFavorites((prev) =>
              prev[key] === !wasFavorited
                ? Object.fromEntries(Object.entries(prev).filter(([entry]) => entry !== key))
                : prev,
            ),
          );
        },
        onRollback: () => setLocalFavorites((prev) => ({ ...prev, [key]: wasFavorited })),
      });
    },
    [current_user, hasFavoritedIdsProp, reloadFavoritedIds],
  );

  const getFavorited: GetFavorited = useCallback(
    (type, id, serverFavorited) => localFavorites[`${type}:${id}`] ?? serverFavorited,
    [localFavorites],
  );

  return { getFavorited, onFavoriteToggle };
}

type FavoritedIds = Record<string, Array<string | number>>;

interface UseFavoritedIdsCallbacksReturn {
  getFavorited: (type: string, id: string | number) => boolean;
  onFavoriteToggle: (type: string, id: string | number) => void;
}

/**
 * Two-argument variant for components that don't know server favorite state:
 * derives `serverFavorited` from the page's `favorited_ids` prop. String
 * comparison absorbs the sqid/number id mismatch across favorable types.
 */
export function useFavoritedIdsCallbacks(favoritedIds: FavoritedIds): UseFavoritedIdsCallbacksReturn {
  const { getFavorited, onFavoriteToggle } = useSearchFavorites();

  // Sets of stringified ids: getFavorited runs per card per render, so the
  // per-type linear scans would be O(cards × favorites) on search pages.
  const favoritedSets = useMemo(() => {
    const sets: Record<string, Set<string>> = {};
    for (const [type, ids] of Object.entries(favoritedIds)) sets[type] = new Set(ids.map(String));
    return sets;
  }, [favoritedIds]);

  const serverFavorited = useCallback(
    (type: string, id: string | number) => favoritedSets[type]?.has(String(id)) ?? false,
    [favoritedSets],
  );

  return {
    getFavorited: useCallback(
      (type: string, id: string | number) => getFavorited(type, id, serverFavorited(type, id)),
      [getFavorited, serverFavorited],
    ),
    onFavoriteToggle: useCallback(
      (type: string, id: string | number) => onFavoriteToggle(type, id, serverFavorited(type, id)),
      [onFavoriteToggle, serverFavorited],
    ),
  };
}

/**
 * Two-argument variant for the favorites pages, where every item starts
 * favorited: pins `serverFavorited` to true so optimistic toggle state layers
 * on top of that baseline.
 */
export function usePageFavorites(): UseFavoritedIdsCallbacksReturn {
  const { getFavorited, onFavoriteToggle } = useSearchFavorites();

  return {
    getFavorited: useCallback((type: string, id: string | number) => getFavorited(type, id, true), [getFavorited]),
    onFavoriteToggle: useCallback(
      (type: string, id: string | number) => onFavoriteToggle(type, id, true),
      [onFavoriteToggle],
    ),
  };
}
